import { useEffect, useState } from 'react';
import { Dropdown, IComboBoxOption, IDropdownOption, IDropdownStyles, Stack, Text, mergeStyles } from '@fluentui/react';
import CustomDatePicker from '../../lib/CustomDatePicker';
import { getLocalizedStrings } from '../../lib/LocalizedStringsForDatePicker';
import { ListHolidays } from './ListHolidays';
import TimeTaxSupportSystemClient from '../../system';
import { IDataCalendarWorkingDay } from '../../system/non_working_days/NonWorkingDays';
import { t } from 'i18next';
import { IEmpresappals } from '../../system/empresappals/EmpresappalsSystem';
import MessageCourier from '../../lib/MessageCourier';

interface ICustomCalendar {
  system: TimeTaxSupportSystemClient;
  selectedCompany: IComboBoxOption | undefined;
  empresappals: IEmpresappals[];
  courier: MessageCourier;
  onCompanyChange: (option: IComboBoxOption) => void;
  onDateChange: (date: Date) => void;
  onRegisterChange?: (isRegister: any) => void;
}

// Estilos personalizados
const calendarContainerStyles = mergeStyles({
  padding: '0px',
  margin: '0',
  width: 'fit-content',
  marginTop: '5px',
  borderSpacing: '0',
  border: '1px solid #ddd',
});

const dayCellStyles = mergeStyles({
  width: '50px',
  height: '40px',
  textAlign: 'center',
  verticalAlign: 'middle',
  // borderBottom: "1px solid #ddd",
});

const holidayCellStyles = mergeStyles(dayCellStyles, {
  backgroundColor: '#D55E5E',
  color: 'white',
  fontWeight: 'bold',
  borderRadius: '50%',
  width: '30px',
  height: '30px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  margin: 'auto',
});

const nonWorkingCellStyles = mergeStyles(dayCellStyles, {
  backgroundColor: '#e9ecef',
  // color: "white",
  // fontWeight: "bold",
  borderRadius: '50%',
  width: '30px',
  height: '30px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  margin: 'auto',
});

const headerCellStyles = mergeStyles(dayCellStyles, {
  fontWeight: 'bold',
});

const legendStyles = mergeStyles({
  marginTop: '20px',
  display: 'flex',
  flexDirection: 'column',
});

const legendItemStyles = mergeStyles({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '10px',
});

const colorBoxStyles = (color: string) =>
  mergeStyles({
    width: '20px',
    height: '20px',
    backgroundColor: color,
    border: '1px solid #ddd',
    borderRadius: '50%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    alignItems: 'center',
    // margin: "auto"
  });

const CustomCalendar = (props: ICustomCalendar) => {
  const [calendarData, setCalendarData] = useState<IDataCalendarWorkingDay>();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isRegister, setIsRegister] = useState<any>(undefined);

  useEffect(() => {
    setSelectedDate(selectedDate);
    props.onDateChange(selectedDate);
    getCalendarData(selectedDate, props.selectedCompany?.key);
  }, [isRegister, props.selectedCompany]);

  const getCalendarData = async (date: Date, companyKey: string | number | undefined) => {
    if (!companyKey) return;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const dateToSend = `${year}-${month}`;
    const data = await props.system.getNonWorkingDaysSystem().getCaldendarWorkingDaysHolidays(dateToSend, companyKey);
    setCalendarData(data);
  };

  const onSelectDate = (date: Date | null | undefined) => {
    if (date) {
      setSelectedDate(date);
      getCalendarData(date, props.selectedCompany?.key);
      props.onDateChange(date);
    }
  };

  const handleRegisterChange = (isRegistered: any) => {
    setIsRegister(isRegistered);

    if (props.onRegisterChange) {
      props.onRegisterChange(isRegistered);
    }
  };

  return (
    <Stack tokens={{ childrenGap: 20 }} style={{ backgroundColor: 'white', padding: '20px' }}>
      <Text variant="xLarge">{t('Configuración feriados')}</Text>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 20 }}>
            <Stack tokens={{ childrenGap: 5 }}>
              <Text variant="mediumPlus">
                <b>{t('Corporación')}</b>
              </Text>
              <Text variant="small">{props.selectedCompany?.text || t("Sin corporación")}</Text>
            </Stack>
            <CustomDatePicker strings={getLocalizedStrings()} label={t('Periodo y Ejercicio')} value={selectedDate} minDate={new Date(2021, 0, 1)} blockedMonths={[]} onSelectDate={onSelectDate} />
          </Stack>
          <br />
          <Stack className="mt-2">
            <Text variant="mediumPlus">
              {t(calendarData?.month || '')} {calendarData?.year}
            </Text>
            {/* Tabla del calendario */}
            <table className={calendarContainerStyles}>
              <thead>
                <tr>
                  {['DO', 'LU', 'MA', 'MI', 'JU', 'VI', 'SA'].map((day) => (
                    <th key={day} className={headerCellStyles}>
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calendarData?.days.map((week, weekIndex) => (
                  <tr key={weekIndex}>
                    {week.map((day, dayIndex) => {
                      if (day === null) {
                        return <td key={dayIndex} className={dayCellStyles}></td>;
                      }

                      let cellStyles = dayCellStyles;
                      if (calendarData.holidays.includes(day)) {
                        cellStyles = holidayCellStyles;
                      } else if (calendarData.nonWorkingDays.includes(day)) {
                        cellStyles = nonWorkingCellStyles;
                      }

                      return calendarData.holidays.includes(day) ? (
                        <td key={dayIndex}>
                          <div className={cellStyles}>{day}</div>
                        </td>
                      ) : (
                        <td key={dayIndex} className={cellStyles}>
                          {day}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        </Stack>

        <Stack>
          <br />
          <div className={legendStyles}>
            <div className={legendItemStyles}>
              <div className={colorBoxStyles('#fff')}></div>
              <Text>{t('Día laboral')}</Text>
            </div>
            <div className={legendItemStyles}>
              <div className={colorBoxStyles('#e9ecef')}></div>
              <Text>{t('Día no laborable')}</Text>
            </div>
            <div className={legendItemStyles}>
              <div className={colorBoxStyles('#D55E5E')}></div>
              <Text>{t('Feriado')}</Text>
            </div>
          </div>
        </Stack>
      </Stack>
      <br />
      <ListHolidays company={props.selectedCompany} date={selectedDate} system={props.system} onRegisterChange={handleRegisterChange} courier={props.courier} />
    </Stack>
  );
};

export default CustomCalendar;
